/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// /** @jsx h */
import { h, Component } from 'preact';

/**
 * Google Photos Picker View Component
 * Implements the new Picker API flow (March 2025+)
 */
interface PickerViewProps {
  provider: any;
  plugin: any;
}

interface PickerViewState {
  session: any;
  isLoading: boolean;
  error: string | null;
  isPolling: boolean;
  mediaItems: any[];
  selectedItems: Set<string>;
  thumbnailUrls: Map<string, string>; // Map of item.id to blob URL
  popupBlocked: boolean;
  isDownloading: boolean;
  downloadProgress: { current: number; total: number };
}

class GooglePhotosPickerView extends Component<PickerViewProps, PickerViewState> {
  private pollInterval: any;

  constructor(props: PickerViewProps) {
    super(props);
    this.state = {
      session: null,
      isLoading: false,
      error: null,
      isPolling: false,
      mediaItems: [],
      selectedItems: new Set(),
      thumbnailUrls: new Map(),
      popupBlocked: false,
      isDownloading: false,
      downloadProgress: { current: 0, total: 0 },
    };
  }

  async componentDidMount() {
    // Wait for provider to be ready and authenticated
    try {
      // Check if already authenticated
      if (this.props.provider.token) {
        this.checkExistingSession();
      } else {
        // Trigger OAuth authentication
        this.setState({ isLoading: true });
        await this.triggerAuthentication();
      }
    } catch (error) {
      console.error('Error in componentDidMount:', error);
      this.setState({ 
        error: 'Failed to initialize Google Photos',
        isLoading: false 
      });
    }
  }

  componentDidUpdate(prevProps) {
    // Check if provider just got authenticated
    if (!prevProps.provider.token && this.props.provider.token) {
      this.checkExistingSession();
    }
  }

  triggerAuthentication = async () => {
    try {
      // Open OAuth popup window
      // Use Unicode-safe base64 encoding (backend expects base64, not URL encoding)
      const stateData = JSON.stringify({ origin: window.location.origin });
      const base64State = btoa(unescape(encodeURIComponent(stateData)));
      const authUrl = `${this.props.provider.opts.companionUrl}/googlePhotos/connect?state=${base64State}`;
      
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2.5;
      
      const popup = window.open(
        authUrl,
        'Google Photos Authentication',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Listen for the OAuth callback
      window.addEventListener('message', this.handleAuthMessage);
    } catch (error) {
      console.error('Error triggering authentication:', error);
      this.setState({
        error: 'Failed to open authentication window. Please allow popups.',
        isLoading: false,
      });
    }
  };

  handleAuthMessage = (event: MessageEvent) => {
    // Validate origin for security
    const expectedOrigin = new URL(this.props.provider.opts.companionUrl).origin;
    if (event.origin !== expectedOrigin) {
      console.warn('Ignoring message from unauthorized origin:', event.origin);
      return;
    }

    console.log('Received message event:', event);
    console.log('Event origin:', event.origin);
    console.log('Window origin:', window.location.origin);
    console.log('Event data:', event.data);
    
    // Parse the token from the message
    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      console.log('Parsed data:', data);
    } catch (error) {
      console.log('Failed to parse message data:', error);
      return;
    }

    if (data && data.token) {
      console.log('Token received:', data.token);
      
      // Store the token in the provider
      this.props.provider.token = data.token;
      
      // Clean up event listener
      window.removeEventListener('message', this.handleAuthMessage);
      
      // Update loading state
      this.setState({ isLoading: true });
      
      // Now proceed with checking session
      this.checkExistingSession();
    } else {
      console.log('No token in message data');
    }
  };

  componentWillUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }

    // Clean up blob URLs to prevent memory leaks
    this.state.thumbnailUrls.forEach((url) => {
      URL.revokeObjectURL(url);
    });
  }

  checkExistingSession = async () => {
    // Don't check for existing session on backend
    // Just create a new one directly
    this.createSession();
  };

  createSession = async () => {
    this.setState({ isLoading: true, error: null });

    try {
      const companionUrl = this.props.provider.opts.companionUrl;
      const response = await fetch(
        `${companionUrl}/googlePhotos/picker/session`,
        {
          method: 'POST',
          headers: {
            'uppy-auth-token': this.props.provider.token || '',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create picker session');
      }

      const session = await response.json();
      this.setState({ session, isLoading: false });

      // Try to automatically open the picker in a new tab
      if (session.pickerUri) {
        const popup = window.open(`${session.pickerUri}/autoclose`, '_blank');
        
        // Detect if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          // Popup was blocked
          this.setState({ popupBlocked: true });
        } else {
          // Popup opened successfully
          this.setState({ popupBlocked: false });
        }
      }

      // Start polling for user selection completion
      this.startPolling();
    } catch (error) {
      console.error('Error creating session:', error);
      this.setState({
        error: 'Failed to create picker session. Please try again.',
        isLoading: false,
      });
    }
  };

  startPolling = () => {
    if (this.state.isPolling) return;

    this.setState({ isPolling: true });

    this.pollInterval = setInterval(async () => {
      try {
        const { session } = this.state;
        if (!session || !session.id) return;

        const companionUrl = this.props.provider.opts.companionUrl;
        const response = await fetch(
          `${companionUrl}/googlePhotos/picker/session/${session.id}`,
          {
            headers: {
              'uppy-auth-token': this.props.provider.token || '',
            },
            credentials: 'include',
          }
        );

        if (response.ok) {
          const updatedSession = await response.json();
          this.setState({ session: updatedSession });

          if (updatedSession.mediaItemsSet) {
            // User completed selection
            clearInterval(this.pollInterval);
            this.setState({ isPolling: false });
            this.loadMediaItems();
          }
        }
      } catch (error) {
        console.error('Error polling session:', error);
      }
    }, 5000); // Poll every 5 seconds
  };

  loadMediaItems = async () => {
    this.setState({ isLoading: true });

    try {
      const { session } = this.state;
      if (!session || !session.id) {
        throw new Error('No session available');
      }

      const companionUrl = this.props.provider.opts.companionUrl;
      const response = await fetch(
        `${companionUrl}/googlePhotos/picker/mediaItems?sessionId=${session.id}&pageSize=100`,
        {
          headers: {
            'uppy-auth-token': this.props.provider.token || '',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load media items');
      }

      const data = await response.json();
      let items = data.mediaItems || [];
      
      // Limit items to maxNumberOfFiles if restriction is set
      const uppy = this.props.plugin.uppy;
      const maxNumberOfFiles = uppy.opts.restrictions?.maxNumberOfFiles;
      
      if (maxNumberOfFiles && maxNumberOfFiles > 0 && maxNumberOfFiles < items.length) {
        items = items.slice(0, maxNumberOfFiles);
      }
      
      // Select all photos by default
      const selectedItemIds = new Set<string>(items.map((item: any) => item.id as string));
      
      this.setState({
        mediaItems: items,
        selectedItems: selectedItemIds,
        isLoading: false,
      });

      // Load thumbnails for all media items
      this.loadThumbnails(items);
    } catch (error) {
      console.error('Error loading media items:', error);
      this.setState({
        error: 'Failed to load selected photos. Please try again.',
        isLoading: false,
      });
    }
  };

  loadThumbnails = async (items: any[]) => {
    const companionUrl = this.props.provider.opts.companionUrl;
    const thumbnailUrls = new Map(this.state.thumbnailUrls);

    for (const item of items) {
      try {
        // Download thumbnail through backend with size parameters
        const response = await fetch(
          `${companionUrl}/googlePhotos/picker/download`,
          {
            method: 'POST',
            headers: {
              'uppy-auth-token': this.props.provider.token || '',
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              baseUrl: item.mediaFile.baseUrl,
              sizeParams: '=w200-h200', // Thumbnail size
            }),
          }
        );

        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          thumbnailUrls.set(item.id, blobUrl);
          
          // Update state to trigger re-render
          this.setState({ thumbnailUrls: new Map(thumbnailUrls) });
        }
      } catch (error) {
        console.error('Error loading thumbnail for', item.id, error);
      }
    }
  };

  openPickerInNewTab = () => {
    if (this.state.session && this.state.session.pickerUri) {
      // Append /autoclose to automatically close the window after selection
      const popup = window.open(`${this.state.session.pickerUri}/autoclose`, '_blank');
      
      if (popup && !popup.closed) {
        // Successfully opened, clear the popup blocked state
        this.setState({ popupBlocked: false });
      }
    }
  };

  selectMedia = (mediaItem: any) => {
    const newSelected = new Set(this.state.selectedItems);
    const id = mediaItem.id;

    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }

    this.setState({ selectedItems: newSelected });
  };

  addSelectedToUppy = async () => {
    const uppy = this.props.plugin.uppy;
    const companionUrl = this.props.provider.opts.companionUrl;
    const { selectedItems, mediaItems } = this.state;
    
    // Add only selected items
    const itemsToAdd = mediaItems.filter(item => selectedItems.has(item.id));
    
    if (itemsToAdd.length === 0) {
      uppy.info('No photos selected', 'warning', 3000);
      return;
    }

    // Show downloading state instead of closing
    this.setState({ 
      isDownloading: true,
      downloadProgress: { current: 0, total: itemsToAdd.length }
    });
    
    // Download and add files one by one
    let successCount = 0;
    for (let i = 0; i < itemsToAdd.length; i++) {
      const mediaItem = itemsToAdd[i];
      
      try {
        // Update progress
        this.setState({
          downloadProgress: { current: i + 1, total: itemsToAdd.length }
        });
        
        // Download the file through backend
        const response = await fetch(
          `${companionUrl}/googlePhotos/picker/download`,
          {
            method: 'POST',
            headers: {
              'uppy-auth-token': this.props.provider.token || '',
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              baseUrl: mediaItem.mediaFile.baseUrl,
              sizeParams: '=d', // Download full size
            }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to download media');
        }

        const blob = await response.blob();
        const file = new File([blob], mediaItem.mediaFile.filename || 'photo.jpg', {
          type: mediaItem.mediaFile.mimeType || 'image/jpeg',
        });

        // Add as a regular local file (like "Upload from my device")
        uppy.addFile({
          name: mediaItem.mediaFile.filename || 'photo.jpg',
          type: mediaItem.mediaFile.mimeType || 'image/jpeg',
          data: file,
          source: 'GooglePhotos',
          isRemote: false,
        });
        
        successCount++;
      } catch (error) {
        console.error('Error adding file to Uppy:', error);
        uppy.info(`Failed to add ${mediaItem.mediaFile.filename}`, 'error', 3000);
      }
    }
    
    // Show success message
    if (successCount > 0) {
      uppy.info(`Added ${successCount} photo${successCount !== 1 ? 's' : ''} from Google Photos`, 'success', 3000);
    }
    
    // Close the provider view after downloads complete
    const dashboard = uppy.getPlugin('Dashboard');
    if (dashboard) {
      (dashboard as any).hideAllPanels();
    }
  };

  render() {
    const { session, isLoading, error, mediaItems, selectedItems, isPolling, popupBlocked, isDownloading, downloadProgress } = this.state;

    if (error) {
      return (
        <div className="uppy-Provider-error">
          <p>{error}</p>
          <button onClick={this.createSession}>Try Again</button>
        </div>
      );
    }

    if (isDownloading) {
      return (
        <div className="uppy-Provider-loading">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <svg 
              width="60" 
              height="60" 
              viewBox="0 0 120 120" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginBottom: '20px' }}
            >
              <circle 
                cx="60" 
                cy="60" 
                r="50" 
                fill="none" 
                stroke="#e0e0e0" 
                strokeWidth="8"
              />
              <circle 
                cx="60" 
                cy="60" 
                r="50" 
                fill="none" 
                stroke="#2275d7" 
                strokeWidth="8"
                strokeDasharray={`${(downloadProgress.current / downloadProgress.total) * 314} 314`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
            </svg>
            <h3 style={{ marginBottom: '10px', fontSize: '18px' }}>
              Downloading Photos...
            </h3>
            <p style={{ color: '#939393', fontSize: '14px' }}>
              {downloadProgress.current} of {downloadProgress.total} photos
            </p>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="uppy-Provider-loading">
          <p>Loading...</p>
        </div>
      );
    }

    if (!session) {
      return (
        <div className="uppy-Provider-loading">
          <p>Initializing Google Photos Picker...</p>
        </div>
      );
    }

    // Show picker URI if user hasn't selected photos yet
    if (!session.mediaItemsSet) {
      return (
        <div className="uppy-Provider-breadcrumbs">
          <div className="uppy-Provider-pickerInstructions" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h2 style={{ marginBottom: '10px' }}>Select Your Photos</h2>
            
              <div>
                <p style={{ color: '#939393', marginBottom: '20px' }}>
                  {isPolling 
                    ? 'Google Photos has opened in a new tab. Select your photos and click Done.'
                    : 'Opening Google Photos...'}
                </p>
                <p style={{ color: '#939393', fontSize: '14px', marginBottom: '20px' }}>
                  This window will automatically update when you're finished.
                </p>
                <button
                  type="button"
                  className="uppy-c-btn uppy-c-btn-primary"
                  onClick={this.openPickerInNewTab}
                  style={{ marginBottom: '10px' }}
                >
                  Open Google Photos Picker
                </button>
              </div>
          </div>
        </div>
      );
    }

    // Show selected media items
    if (mediaItems.length === 0) {
      return (
        <div className="uppy-Provider-breadcrumbs">
          <p>No photos selected. Please select photos using the picker.</p>
          <button 
            type="button"
            className="uppy-c-btn uppy-c-btn-primary"
            onClick={this.createSession}
          >
            Start New Selection
          </button>
        </div>
      );
    }

    return (
      <div className="uppy-ProviderBrowser uppy-ProviderBrowser-viewType--grid">
        {/* Scrollable photo grid */}
        <div className="uppy-ProviderBrowser-body">
          <ul className="uppy-ProviderBrowser-list" role="listbox">
            {mediaItems.map((item) => {
              const isSelected = selectedItems.has(item.id);
              const thumbnailUrl = this.state.thumbnailUrls.get(item.id);
              
              return (
                <li
                  key={item.id}
                  className={`uppy-ProviderBrowserItem ${isSelected ? 'uppy-ProviderBrowserItem--selected' : ''}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <button
                    type="button"
                    className="uppy-u-reset uppy-ProviderBrowserItem-inner"
                    onClick={() => this.selectMedia(item)}
                    aria-label={`${isSelected ? 'Deselect' : 'Select'} ${item.mediaFile.filename || 'Photo'}`}
                  >
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={item.mediaFile.filename || 'Photo'}
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#939393', fontSize: '11px' }}>
                        Loading...
                      </div>
                    )}
                    
                    <div className={`uppy-ProviderBrowserItem-fakeCheckbox ${isSelected ? 'uppy-ProviderBrowserItem-fakeCheckbox--is-checked' : ''}`} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        
        {/* Fixed footer with Add button */}
        <div className="uppy-ProviderBrowser-footer">
          <button
            onClick={this.addSelectedToUppy}
            disabled={selectedItems.size === 0}
            className="uppy-u-reset uppy-c-btn uppy-c-btn-primary"
          >
            Select {selectedItems.size}
          </button>
          
        </div>
      </div>
    );
  }
}

export default GooglePhotosPickerView;

